import { useState, useMemo, type ReactNode } from 'react';
import { COLORS } from '../../constants/theme';

export type SortDir = 'asc' | 'desc' | 'none';

export interface ColumnDef<TRow> {
  key: string;
  header: string;
  render?: (row: TRow) => ReactNode;
  sortable?: boolean;
  /** Returns a numeric or string value for sort comparison. */
  sortAccessor?: (row: TRow) => string | number;
  /** If true, renders as a sticky first column. */
  sticky?: boolean;
}

export interface SortState {
  key: string;
  dir: SortDir;
}

interface SortableTableProps<TRow> {
  columns: ColumnDef<TRow>[];
  rows: TRow[];
  getRowKey: (row: TRow) => string;
  sortState: SortState;
  onSort: (key: string) => void;
  onRowClick?: (row: TRow) => void;
}

/**
 * Generic sortable table — relocated from `components/dossier/DossierTable.tsx` (SF-169),
 * `components/ui/` being the established home for generic primitives in this codebase.
 *
 * - Typed generics: SortableTable<TRow> accepts ColumnDef<TRow>[]
 * - Sticky first column: must have opaque backgroundColor to prevent scroll-bleed
 * - aria-sort on sortable column headers (WAI-ARIA)
 * - Sort cycles: none → asc → desc → none on repeated clicks
 * - SF-169 Decision 6: sortable header content is wrapped in a real <button> (inside the
 *   <th scope="col">, which keeps aria-sort) so Enter/Space activate it via native button
 *   semantics — no new key handler needed.
 */
export function SortableTable<TRow>({
  columns,
  rows,
  getRowKey,
  sortState,
  onSort,
  onRowClick,
}: SortableTableProps<TRow>) {
  const sortedRows = useMemo(() => {
    if (sortState.dir === 'none') return rows;
    const col = columns.find((c) => c.key === sortState.key);
    if (!col?.sortAccessor) return rows;
    const accessor = col.sortAccessor;
    return [...rows].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return sortState.dir === 'asc' ? -1 : 1;
      if (av > bv) return sortState.dir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, sortState, columns]);

  const STICKY_BG = COLORS.card;
  const STICKY_BG_ALT = COLORS.tableRowAlt;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13,
          color: COLORS.text.secondary,
        }}
      >
        <thead>
          <tr style={{ backgroundColor: COLORS.sectionHeader }}>
            {columns.map((col) => {
              const isSorted = sortState.key === col.key && sortState.dir !== 'none';
              const ariaSortValue = isSorted
                ? sortState.dir === 'asc'
                  ? ('ascending' as const)
                  : ('descending' as const)
                : ('none' as const);

              return (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={col.sortable ? ariaSortValue : undefined}
                  style={{
                    padding: '8px 12px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: COLORS.text.tertiary,
                    whiteSpace: 'nowrap',
                    borderBottom: `1px solid ${COLORS.border}`,
                    ...(col.sticky
                      ? {
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          backgroundColor: COLORS.sectionHeader,
                        }
                      : {}),
                  }}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        font: 'inherit',
                        color: 'inherit',
                        cursor: 'pointer',
                        userSelect: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      {col.header}
                      <span aria-hidden="true" style={{ marginLeft: 4, opacity: 0.6 }}>
                        {isSorted ? (sortState.dir === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, idx) => {
            const isAlt = idx % 2 === 1;
            const rowBg = isAlt ? STICKY_BG_ALT : STICKY_BG;
            return (
              <tr
                key={getRowKey(row)}
                style={{ backgroundColor: rowBg, cursor: onRowClick ? 'pointer' : undefined }}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    style={{
                      padding: '6px 12px',
                      borderBottom: `1px solid ${COLORS.border}`,
                      whiteSpace: 'nowrap',
                      ...(col.sticky
                        ? {
                            position: 'sticky',
                            left: 0,
                            zIndex: 1,
                            backgroundColor: rowBg,
                          }
                        : {}),
                    }}
                  >
                    {col.render ? col.render(row) : String((row as Record<string, unknown>)[col.key] ?? '—')}
                  </td>
                ))}
              </tr>
            );
          })}
          {sortedRows.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                style={{ padding: '24px', textAlign: 'center', color: COLORS.text.muted }}
              >
                No data
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Helper to cycle sort direction: none → asc → desc → none */
export function cycleSortDir(current: SortDir): SortDir {
  if (current === 'none') return 'asc';
  if (current === 'asc') return 'desc';
  return 'none';
}

/** Hook for managing sort state per-tab. Returns the sort state and an onSort handler. */
export function useSortState(initialKey = '', initialDir: SortDir = 'none') {
  const [sortState, setSortState] = useState<SortState>({ key: initialKey, dir: initialDir });

  const handleSort = (key: string) => {
    setSortState((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      return { key, dir: cycleSortDir(prev.dir) };
    });
  };

  return { sortState, handleSort };
}
